import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShoppingBag, Shield, AlertCircle, Users, Package, ArrowRight, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { useState } from "react";
import { formatMoney } from "../../utils/formatMoney";

const POLICY_TONES = [
  {
    border: "border-emerald-200",
    header: "bg-emerald-50",
    title: "text-emerald-950",
    muted: "text-emerald-800",
    icon: "text-emerald-600",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md",
    pkg: "bg-emerald-50 border-emerald-200",
    selected: "bg-emerald-100 border-emerald-500 shadow-md",
    idle: "bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:shadow-md",
    badge: "bg-emerald-600",
    dashed: "border-emerald-300 bg-emerald-50/70",
    success: "bg-emerald-50 border border-emerald-200",
    pulse: "bg-emerald-400",
    cover: "bg-emerald-100 text-emerald-800",
  },
  {
    border: "border-orange-200",
    header: "bg-orange-50",
    title: "text-orange-950",
    muted: "text-orange-800",
    icon: "text-orange-600",
    btn: "bg-orange-600 hover:bg-orange-700 text-white shadow-sm hover:shadow-md",
    pkg: "bg-orange-50 border-orange-200",
    selected: "bg-orange-100 border-orange-500 shadow-md",
    idle: "bg-orange-50 border-orange-200 hover:border-orange-400 hover:shadow-md",
    badge: "bg-orange-600",
    dashed: "border-orange-300 bg-orange-50/70",
    success: "bg-orange-50 border border-orange-200",
    pulse: "bg-orange-400",
    cover: "bg-orange-100 text-orange-800",
  },
  {
    border: "border-slate-200",
    header: "bg-slate-50",
    title: "text-slate-900",
    muted: "text-slate-700",
    icon: "text-slate-600",
    btn: "bg-slate-800 hover:bg-slate-900 text-white shadow-sm hover:shadow-md",
    pkg: "bg-slate-50 border-slate-200",
    selected: "bg-slate-100 border-slate-500 shadow-md",
    idle: "bg-slate-50 border-slate-200 hover:border-slate-400 hover:shadow-md",
    badge: "bg-slate-800",
    dashed: "border-slate-300 bg-slate-50/70",
    success: "bg-slate-50 border border-slate-200",
    pulse: "bg-slate-400",
    cover: "bg-slate-100 text-slate-800",
  },
];

export default function ComparisonDrawer({ open, onClose, policies = [] }) {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  // Track selected route/package - only one can be selected at a time across all policies
  const [selectedItem, setSelectedItem] = useState(null);

  const visiblePolicies = policies.slice(0, 3);

  if (!open || visiblePolicies.length < 2) return null;

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return formatMoney(amount);
  };

  // Helper to format duration (e.g., "1 monthly" -> "1 month", "2 yearly" -> "2 years")
  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    
    const durationStr = String(duration).toLowerCase();
    
    // Match pattern like "1 monthly", "2 yearly", "3 weekly", etc.
    const match = durationStr.match(/^(\d+)\s*(daily|weekly|monthly|yearly|day|week|month|year)s?$/);
    
    if (!match) return duration; // Return as-is if no match
    
    const value = parseInt(match[1]);
    let unit = match[2];
    
    // Normalize unit names
    const unitMap = {
      'daily': 'day',
      'weekly': 'week', 
      'monthly': 'month',
      'yearly': 'year'
    };
    
    unit = unitMap[unit] || unit;
    
    // Pluralize if value > 1
    if (value > 1) {
      unit = unit + 's';
    }
    
    return `${value} ${unit}`;
  };

  // Helper to normalize premium duration for display (yearly -> year, monthly -> month, etc.)
  const normalizePremiumDuration = (duration) => {
    if (!duration) return null;
    const durationStr = String(duration).toLowerCase().trim();
    const durationMap = {
      'yearly': 'year',
      'monthly': 'month',
      'weekly': 'week',
      'daily': 'day'
    };
    return durationMap[durationStr] || durationStr;
  };

  // Helper to check if policy is TripSecure
  const isTripSecure = (policy) => {
    return policy.shortDescription === "tripsecure_insurance";
  };

  // Helper to get packages from policy (for Life, General, Travel)
  const getPackages = (policy) => {
    if (!policy.originalPolicy?.coverage_durations) return [];
    
    const isLifeInsurance = policy.shortDescription === "life_insurance";
    const isGeneralInsurance = policy.shortDescription === "general_insurance";
    const isTravelInsurance = policy.shortDescription === "travel_insurance";

    if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
      return policy.originalPolicy.coverage_durations;
    }
    
    return [];
  };

  // Helper to get routes for TripSecure insurance
  const getRoutes = (policy) => {
    if (!isTripSecure(policy)) return [];
    return policy.originalPolicy?.coverage_durations || [];
  };

  const handleSelectItem = (policyId, item) => {
    setSelectedItem((prev) => {
      if (prev?.policyId === policyId && prev?.item === item) return null;
      return { policyId, item };
    });
  };

  // Handle purchase
  const handlePurchase = (policy) => {
    const packages = getPackages(policy);
    const isTripSecurePolicy = isTripSecure(policy);
    const policySelectedItem = selectedItem?.policyId === policy.id ? selectedItem.item : null;
    
    if (packages.length > 0) {
      // For Life, General, Travel with packages - open drawer to select package
      onClose();
      if (window.openPolicyPackageDrawer) {
        window.openPolicyPackageDrawer(policy);
      }
    } else if (isTripSecurePolicy && policySelectedItem) {
      // For TripSecure with selected route - pass route information
      // Extract insurance_provider_slug
      const insuranceProviderSlug = policy?.originalPolicy?.insurance_provider 
        ? (typeof policy.originalPolicy.insurance_provider === 'object' 
            ? policy.originalPolicy.insurance_provider.slug || policy.originalPolicy.insurance_provider.id || null
            : policy.originalPolicy.insurance_provider)
        : null;
      const details = {
        policyType: policy.originalPolicy?.policy_type || "intercity_travel",
        providerName: policy.provider || "",
        policyName: policy.policyName || "",
        policySlug: policy.originalPolicy?.slug || policy.id,
        originalPolicy: policy.originalPolicy,
        coverageDuration: policySelectedItem, // Pass the selected route as coverageDuration
        insurance_provider_slug: insuranceProviderSlug,
      };
      
      if (!isAuthenticated) {
        sessionStorage.setItem("pendingCheckout", JSON.stringify(details));
        navigate("/signin", { state: { from: "/checkout" } });
        return;
      }
      navigate("/checkout", { state: details });
    } else {
      // For policies without packages and no selection required
      // Extract insurance_provider_slug
      const insuranceProviderSlug = policy?.originalPolicy?.insurance_provider 
        ? (typeof policy.originalPolicy.insurance_provider === 'object' 
            ? policy.originalPolicy.insurance_provider.slug || policy.originalPolicy.insurance_provider.id || null
            : policy.originalPolicy.insurance_provider)
        : null;
      const details = {
        policyType: policy.originalPolicy?.policy_type || "daily_commuter",
        providerName: policy.provider || "",
        policyName: policy.policyName || "",
        policySlug: policy.originalPolicy?.slug || policy.id,
        originalPolicy: policy.originalPolicy,
        insurance_provider_slug: insuranceProviderSlug,
      };
      
      if (!isAuthenticated) {
        sessionStorage.setItem("pendingCheckout", JSON.stringify(details));
        navigate("/signin", { state: { from: "/checkout" } });
        return;
      }
      navigate("/checkout", { state: details });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="comparison-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed inset-0 z-[70] flex flex-col overflow-hidden bg-white sm:inset-3 sm:rounded-2xl sm:shadow-2xl"
          >
            <div className="flex-shrink-0 border-b border-emerald-700 bg-emerald-600 px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-white">
                    <Shield className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 id="comparison-title" className="text-lg font-bold text-white sm:text-xl">
                      Compare policies
                    </h2>
                    <p className="text-sm text-emerald-50">
                      Side by side, up to 3 policies
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  aria-label="Close comparison"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-gray-50 p-4 sm:p-6">
              <div className={visiblePolicies.length >= 3 ? "overflow-x-auto pb-1" : ""}>
              <div
                className={`grid gap-4 ${
                  visiblePolicies.length >= 3
                    ? "min-w-[60rem] grid-cols-3"
                    : "grid-cols-1 lg:grid-cols-2"
                }`}
              >
                {visiblePolicies.map((policy, policyIdx) => {
                  const packages = getPackages(policy);
                  const routes = getRoutes(policy);
                  const isTripSecurePolicy = isTripSecure(policy);
                  const hasSelection = selectedItem?.policyId === policy.id;
                  const requiresSelection = (isTripSecurePolicy && routes.length > 0) || packages.length > 0;
                  const canProceed = !requiresSelection || hasSelection || packages.length > 0;
                  
                  const a = POLICY_TONES[policyIdx % POLICY_TONES.length];
                  
                  return (
                    <motion.div
                      key={policy.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`overflow-hidden bg-white rounded-xl border-2 shadow-lg ${a.border}`}
                    >
                      {/* Policy Header */}
                      <div className={`p-6 ${a.header}`}>
                        <h3 className={`mb-2 text-xl font-bold ${a.title}`}>
                          {policy.policyName}
                        </h3>
                        <p className={`text-sm ${a.muted}`}>
                          {policy.provider}
                        </p>
                        <div className="mt-4">
                          {packages.length > 0 && packages.every((p) => p.is_quote_only) ? (
                            <>
                              <span className="text-2xl font-bold text-amber-700">
                                Price on quote
                              </span>
                              <p className="mt-1 text-sm text-amber-600">
                                Quote-based packages
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex gap-2 items-baseline">
                                <span className={`text-3xl font-bold ${a.title}`}>
                                  {formatCurrency(policy.premiumMin)}
                                </span>
                                {policy.premiumMax && policy.premiumMax !== policy.premiumMin && (
                                  <span className={a.muted}>
                                    {" "}- {formatCurrency(policy.premiumMax)}
                                  </span>
                                )}
                              </div>
                              <p className={`mt-1 text-sm ${a.muted}`}>
                                Premium Range
                              </p>
                            </>
                          )}
                        </div>
                        {/* Selection Guideline */}
                        {requiresSelection && !hasSelection && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`mt-4 p-3 rounded-lg border-2 border-dashed ${a.dashed}`}
                          >
                            <div className="flex gap-2 items-start">
                              {isTripSecurePolicy ? (
                                <MapPin className={`mt-0.5 h-4 w-4 ${a.icon}`} aria-hidden="true" />
                              ) : (
                                <Package className={`mt-0.5 h-4 w-4 ${a.icon}`} aria-hidden="true" />
                              )}
                              <div>
                                <p className={`text-xs font-semibold ${a.title}`}>
                                  {isTripSecurePolicy ? "Select Your Route" : "Choose a Package"}
                                </p>
                                <p className={`text-[11px] mt-0.5 ${a.muted}`}>
                                  {isTripSecurePolicy 
                                    ? "Click on any route below to activate the purchase button"
                                    : "Click on a package below to continue"}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Success Message */}
                        {hasSelection && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={`mt-4 p-3 rounded-lg ${a.success}`}
                          >
                            <div className="flex gap-2 items-center">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                a.badge
                              }`}>
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className={`text-xs font-semibold ${a.title}`}>
                                  Selection Confirmed
                                </p>
                                <p className={`text-[11px] ${a.muted}`}>
                                  Ready to proceed to checkout
                                </p>
                                <p className={`mt-1 text-[10px] italic ${a.icon}`}>
                                  Click the selected route again to deselect
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Purchase Button */}
                        <button
                          type="button"
                          onClick={() => handlePurchase(policy)}
                          disabled={!canProceed}
                          className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                            canProceed
                              ? a.btn
                              : "cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400"
                          }`}
                        >
                          <ShoppingBag className="h-4 w-4" />
                          <span>{packages.length > 0 ? "View Packages" : "Select This Policy"}</span>
                          {canProceed && <ArrowRight className="h-4 w-4" />}
                        </button>
                      </div>

                      {/* Policy Content */}
                      <div className="p-6 space-y-6">
                        {/* Description */}
                        {/* <div>
                          <div className="flex gap-2 items-center mb-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-gray-900">Description</h4>
                          </div>
                          <p className="text-sm leading-relaxed text-gray-700">
                            {policy.description || policy.originalPolicy?.description || "No description available"}
                          </p>
                        </div> */}

                        {/* Packages */}
                          {packages.length > 0 && (
                            <div>
                              <div className="flex gap-2 items-center mb-3">
                                <Package className={`w-5 h-5 ${a.icon}`} />
                                <h4 className="font-semibold text-gray-900">
                                  Available Packages ({packages.length})
                                </h4>
                              </div>
                              <div className="space-y-4">
                                {packages.map((pkg, idx) => (
                                  <div key={idx} className={`rounded-lg border p-4 ${a.pkg}`}>
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <h5 className="font-bold text-gray-900">{pkg.product_name}</h5>
                                        {pkg.is_quote_only && (
                                          <span className="px-2 py-0.5 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full">
                                            Request quote
                                          </span>
                                        )}
                                      </div>
                                      <p className="mt-1 text-sm text-gray-600">{pkg.description}</p>
                                    </div>
                                    <div className="ml-4 text-right">
                                      <div className="flex flex-col items-end">
                                        {pkg.is_quote_only ? (
                                          <div className="text-base font-bold text-amber-600">
                                            Price on quote
                                          </div>
                                        ) : (
                                          <>
                                            <div className={`text-2xl font-bold ${a.icon}`}>
                                              ₵{pkg.premium}
                                            </div>
                                            {pkg.premium_duration && (
                                              <div className="text-xs text-gray-500 capitalize">
                                                per {normalizePremiumDuration(pkg.premium_duration)}
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <div className={`text-xs font-medium mt-1 ${a.muted}`}>
                                        Coverage: {formatDuration(pkg.duration)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Package Benefits */}
                                  {pkg.insurance_coverage_list && pkg.insurance_coverage_list.length > 0 && (
                                    <div className={`pt-3 mt-3 border-t ${a.border}`}>
                                      <p className={`mb-2 text-xs font-semibold ${a.title}`}>
                                        Coverage Benefits:
                                      </p>
                                      <ul className="space-y-2">
                                        {pkg.insurance_coverage_list.map((benefit, bIdx) => (
                                          <li key={bIdx} className="flex gap-2 items-start text-xs">
                                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                              <span className="font-medium text-gray-900">{benefit.title}</span>
                                              {benefit.limit && (
                                                <span className="ml-2 font-bold text-emerald-600">
                                                  (₵{benefit.limit})
                                                </span>
                                              )}
                                              {benefit.description && (
                                                <p className="text-gray-600 mt-0.5">{benefit.description}</p>
                                              )}
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Package Eligibility */}
                                  {pkg.eligibility_criteria && pkg.eligibility_criteria.length > 0 && (
                                    <div className={`pt-3 mt-3 border-t ${a.border}`}>
                                      <p className={`mb-2 text-xs font-semibold ${a.title}`}>
                                        Eligibility:
                                      </p>
                                      <ul className="space-y-1">
                                        {pkg.eligibility_criteria.map((criteria, cIdx) => (
                                          <li key={cIdx} className="flex gap-2 items-start text-xs text-gray-700">
                                            <Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-emerald-600" />
                                            <span>{criteria}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Package Exclusions */}
                                  {pkg.exclusions && pkg.exclusions.length > 0 && (
                                    <div className={`pt-3 mt-3 border-t ${a.border}`}>
                                      <p className={`mb-2 text-xs font-semibold ${a.title}`}>
                                        Exclusions:
                                      </p>
                                      <ul className="space-y-1">
                                        {pkg.exclusions.map((exclusion, eIdx) => (
                                          <li key={eIdx} className="flex gap-2 items-start text-xs text-gray-700">
                                            <X className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                                            <span>{exclusion}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Package Claim Process */}
                                  {pkg.claim_process_steps && pkg.claim_process_steps.length > 0 && (
                                    <div className={`pt-3 mt-3 border-t ${a.border}`}>
                                      <p className={`mb-2 text-xs font-semibold ${a.title}`}>
                                        Claim Process:
                                      </p>
                                      <ol className="space-y-2">
                                        {pkg.claim_process_steps.map((step, sIdx) => (
                                          <li key={sIdx} className="flex gap-2 items-start text-xs">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">
                                              {step.step_number || sIdx + 1}
                                            </span>
                                            <div className="flex-1">
                                              <span className="font-medium text-gray-900">{step.title}</span>
                                              <p className="text-gray-600 mt-0.5">{step.description}</p>
                                              {step.timeframe && (
                                                <span className="text-blue-600 text-[10px] mt-1 block">
                                                  ⏱ {step.timeframe}
                                                </span>
                                              )}
                                            </div>
                                          </li>
                                        ))}
                                      </ol>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* TripSecure Routes */}
                        {isTripSecurePolicy && routes.length > 0 && (
                          <div>
                            <div className="flex gap-2 items-center mb-3">
                              <Package className={`w-5 h-5 ${a.icon}`} />
                              <h4 className="font-semibold text-gray-900">
                                Available Routes ({routes.length})
                              </h4>
                            </div>
                            <div className={`grid grid-cols-1 gap-3 ${visiblePolicies.length < 3 ? "sm:grid-cols-2" : ""}`}>
                              {routes.map((route, idx) => {
                                // Extract destination region from route_destination
                                // route_destination can be like "Western Region" or "Greater Accra - Western Region"
                                const destinationRegion = route.route_destination || "";
                                const routeLabel = destinationRegion.includes(" - ") 
                                  ? destinationRegion.split(" - ")[1] || destinationRegion
                                  : destinationRegion;
                                
                                const isSelected = selectedItem?.policyId === policy.id && selectedItem?.item === route;

                                return (
                                  <motion.button
                                    type="button"
                                    key={idx}
                                    aria-pressed={isSelected}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`relative rounded-lg border-2 p-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                      isSelected
                                        ? `${a.selected} cursor-pointer`
                                        : `${a.idle} cursor-pointer`
                                    }`}
                                    onClick={() => handleSelectItem(policy.id, route)}
                                  >
                                    {/* Selection Indicator */}
                                    {isSelected && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                                          a.badge
                                        }`}
                                      >
                                        <Check className="w-4 h-4 text-white" />
                                      </motion.div>
                                    )}

                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="mb-1">
                                          <p className="text-xs text-gray-500 mb-0.5">From</p>
                                          <p className="text-xs font-semibold text-gray-700">Greater Accra</p>
                                        </div>
                                        <div className="mb-1">
                                          <p className="text-xs text-gray-500 mb-0.5">To</p>
                                          <h5 className={`text-sm font-bold transition-colors ${
                                            isSelected 
                                              ? a.title
                                              : "text-gray-900"
                                          }`}>
                                            {routeLabel}
                                          </h5>
                                        </div>
                                        <p className={`text-xs mt-1 capitalize font-medium ${
                                          isSelected
                                            ? a.muted
                                            : "text-gray-600"
                                        }`}>
                                          {route.route_type} Route
                                        </p>
                                        {!isSelected && (
                                          <p className="mt-1 flex items-center gap-1 text-[10px] italic text-gray-500">
                                            <span className={`h-1.5 w-1.5 rounded-full ${a.pulse} animate-pulse`} />
                                            Click to select
                                          </p>
                                        )}
                                      </div>
                                      <div className="ml-2 text-right">
                                        <div className={`text-lg font-bold transition-colors ${
                                          isSelected
                                            ? a.muted
                                            : a.icon
                                        }`}>
                                          ₵{route.price}
                                        </div>
                                        <div className={`text-xs font-medium ${
                                          isSelected
                                            ? a.icon
                                            : a.muted
                                        }`}>
                                          {route.display_value} {route.display_unit}
                                        </div>
                                      </div>
                                    </div>
                                  </motion.button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Policy-Level Info (for TripSecure and policies without packages) */}
                        {(isTripSecurePolicy || packages.length === 0) && (
                          <>
                            {/* Coverage Benefits (for TripSecure) */}
                            {isTripSecurePolicy && policy.originalPolicy?.insurance_coverage_list && policy.originalPolicy.insurance_coverage_list.length > 0 && (
                              <div>
                                <div className="flex gap-2 items-center mb-3">
                                  <Shield className={`w-5 h-5 ${a.icon}`} />
                                  <h4 className="font-semibold text-gray-900">Coverage Benefits</h4>
                                </div>
                                <ul className="space-y-2">
                                  {policy.originalPolicy.insurance_coverage_list.map((benefit, bIdx) => (
                                    <li key={bIdx} className="flex gap-2 items-start text-sm">
                                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1">
                                        <span className="font-medium text-gray-900">{benefit.title}</span>
                                        {benefit.coverage_type && (
                                          <span className={`ml-2 rounded px-2 py-0.5 text-xs font-semibold ${
                                            benefit.coverage_type === "regional"
                                              ? "bg-emerald-100 text-emerald-800"
                                              : "bg-orange-100 text-orange-800"
                                          }`}>
                                            {benefit.coverage_type}
                                          </span>
                                        )}
                                        {benefit.limit && (
                                          <span className="ml-2 font-bold text-emerald-600">
                                            (₵{benefit.limit})
                                          </span>
                                        )}
                                        {benefit.description && (
                                          <p className="text-gray-600 mt-0.5">{benefit.description}</p>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Eligibility */}
                            {(policy.eligibilityCriteria || policy.originalPolicy?.eligibility_criteria) && (
                              <div>
                                <div className="flex gap-2 items-center mb-2">
                                  <Users className="h-5 w-5 text-emerald-600" />
                                  <h4 className="font-semibold text-gray-900">Eligibility</h4>
                                </div>
                                <ul className="space-y-2">
                                  {(policy.eligibilityCriteria || policy.originalPolicy?.eligibility_criteria || []).map((criteria, idx) => (
                                    <li key={idx} className="flex gap-2 items-start text-sm text-gray-700">
                                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                      <span>{criteria}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Exclusions */}
                            {(policy.exclusions || policy.originalPolicy?.exclusions) && (
                              <div>
                                <div className="flex gap-2 items-center mb-2">
                                  <AlertCircle className="w-5 h-5 text-red-600" />
                                  <h4 className="font-semibold text-gray-900">Exclusions</h4>
                                </div>
                                <ul className="space-y-2">
                                  {(policy.exclusions || policy.originalPolicy?.exclusions || []).map((exclusion, idx) => (
                                    <li key={idx} className="flex gap-2 items-start text-sm text-gray-700">
                                      <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                      <span>{typeof exclusion === 'string' ? exclusion : exclusion.text || exclusion}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Claim Process (for TripSecure) */}
                            {isTripSecurePolicy && policy.originalPolicy?.claim_process_steps && policy.originalPolicy.claim_process_steps.length > 0 && (
                              <div>
                                <div className="flex gap-2 items-center mb-3">
                                  <Shield className={`w-5 h-5 ${a.icon}`} />
                                  <h4 className="font-semibold text-gray-900">Claim Process</h4>
                                </div>
                                <ol className="space-y-3">
                                  {policy.originalPolicy.claim_process_steps.map((step, sIdx) => (
                                    <li key={sIdx} className="flex gap-3 items-start text-sm">
                                      <span className={`flex-shrink-0 w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold ${
                                        a.badge
                                      }`}>
                                        {step.step_number || sIdx + 1}
                                      </span>
                                      <div className="flex-1">
                                        <span className="font-semibold text-gray-900">{step.title}</span>
                                        <p className="mt-1 text-gray-600">{step.description}</p>
                                        {step.timeframe && (
                                          <div className="flex gap-4 mt-2 text-xs">
                                            <span className={a.icon}>
                                              ⏱ {step.timeframe}
                                            </span>
                                          </div>
                                        )}
                                        {step.requirements && (
                                          <p className="mt-1 text-xs text-gray-500">
                                            📋 {step.requirements}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <p className="text-sm text-gray-600">
                  Review the details above and select the policy that best suits your needs
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  Close comparison
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

